import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Sidebar collapsed/expanded state. Persists per-browser so the user's
 * choice survives a reload. Expanded = 232px, collapsed = 64px (icons-only).
 */
interface SidebarState {
  collapsed: boolean;
  toggle: () => void;
  setCollapsed: (c: boolean) => void;
}

function apply(collapsed: boolean): void {
  if (typeof document === 'undefined') return;
  if (collapsed) document.documentElement.setAttribute('data-sidebar', 'collapsed');
  else           document.documentElement.removeAttribute('data-sidebar');
}

export const useSidebarStore = create<SidebarState>()(
  persist(
    (set, get) => ({
      collapsed: false,
      toggle: () => {
        const next = !get().collapsed;
        apply(next);
        set({ collapsed: next });
      },
      setCollapsed: (c) => {
        apply(c);
        set({ collapsed: c });
      },
    }),
    {
      name: 'lab.sidebar',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // Sur mobile (≤ 480px), on force le drawer fermé au démarrage
        // pour ne pas masquer le contenu — l'utilisateur l'ouvre via le burger.
        const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 480px)').matches;
        const collapsed = isMobile ? true : state.collapsed;
        if (collapsed !== state.collapsed) state.collapsed = collapsed;
        apply(collapsed);
      },
    },
  ),
);
